import { useStripe, useElements } from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';
import { useStateSelector } from '../../../store/selectors';
import { PaymentDialog, PriceItem } from '../PaymentDialog';
import createNotification from '../../../utils/createNotification';
import { TimelineTempo } from '../../../slices/marketplace/timeline/models/enums/timeline-tempo.enum';

interface ChangeTempoFeePaymentComponentProps {
    isVisible: boolean;
    setIsVisible: (isVisible: React.SetStateAction<boolean>) => void;
    paymentSuccess: (transactionId: string) => void;
    engagementId: number;
    feeAmount: number;
    fteDvms: number;
    totalPrice: number;
    selectedTempo: TimelineTempo;
}

const ChangeTempoFeePaymentComponent = (
    props: ChangeTempoFeePaymentComponentProps
) => {
    const axios = useStateSelector((state) => state.core.axios);
    const stripe = useStripe();
    const elements = useElements();

    const [originalServicePrice, setOriginalServicePrice] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [appliedCredits, setAppliedCredits] = useState(0);
    const [isPaymentInProcess, setIsPaymentInProcess] = useState(false);

    useEffect(() => {
        setOriginalServicePrice(props.totalPrice);
    }, [props.totalPrice]);

    const calculateTotalPrice = () => originalServicePrice - appliedCredits;

    useEffect(() => {
        setTotalPrice(calculateTotalPrice());
    }, [appliedCredits, originalServicePrice]);

    const priceList: PriceItem[] = [
        {
            priceType: 'summary',
            label: 'Change Tempo Fee',
        },
        {
            priceType: 'regular',
            label: 'Fee',
            value: props.feeAmount,
            valueType: 'currency',
        },
        {
            priceType: 'regular',
            label: 'Seller FTE DVMs:',
            value: props.fteDvms,
            valueType: 'number',
        },
        {
            priceType: 'summary',
            label: 'Total Due:',
            value: props.totalPrice,
            valueType: 'currency',
        },
    ];

    const directPayment = () => payChangeTempoFee(false);

    const payChangeTempoFee = (
        shouldUseSavedPaymentMethod: boolean,
        paymentTokenId: string = ''
    ) => {
        setIsPaymentInProcess(true);
        axios
            .post('/api/MarketplaceCommerce/PayTempoChangeFee', {
                walletId: 0,
                engagementId: props.engagementId,
                useWalletBalance: !!appliedCredits,
                walletAmount: appliedCredits,
                useSavedPaymentMethod: shouldUseSavedPaymentMethod,
                source: paymentTokenId,
                totalPrice: originalServicePrice,
                selectedTempo: props.selectedTempo,
            })
            .then((response: any) => {
                if (response?.response?.status == 402) {
                    createNotification(
                        response.response.data?.title ??
                            'An unexpected error occured. Please try again later',
                        'error'
                    );
                    setIsPaymentInProcess(false);
                    props.setIsVisible(false);
                } else {
                    handlePricingUpdatedError(response);
                    setTimeout(() => {
                        setIsPaymentInProcess(false);
                        props.paymentSuccess(response.data);
                    }, 3000);
                }
            })
            .catch((err: any) => console.log(err));
    };

    const handlePricingUpdatedError = (response: any) => {
        if (response.response?.status === 422) {
            createNotification(
                'Tempo Change Fee has been changed, please try again',
                'error'
            );
            setIsPaymentInProcess(false);
            props.setIsVisible(false);
            throw new Error('Price changed');
        }

        if (response.response?.status === 500) {
            createNotification(
                response.response.data?.title ??
                    'An unexpected error occured. Please try again later',
                'error'
            );
            setIsPaymentInProcess(false);
            props.setIsVisible(false);
            throw new Error('Bad request');
        }
    };

    return (
        <div className="change-tempo-fee-payment-component">
            <PaymentDialog
                isVisible={props.isVisible}
                setIsVisible={props.setIsVisible}
                stripe={stripe}
                elements={elements}
                priceList={priceList}
                directPayment={directPayment}
                originalServicePrice={originalServicePrice}
                servicePrice={originalServicePrice}
                totalProductPrice={totalPrice}
                apliedCredits={appliedCredits}
                setAppliedCredits={(value) => setAppliedCredits(value)}
                chargeRequest={payChangeTempoFee}
                isParrentLoading={false}
                isPaymentInProcess={isPaymentInProcess}
                isAuthorizationRequired={false}
            />
        </div>
    );
};

export default ChangeTempoFeePaymentComponent;
